import React, { useState, useEffect, useRef } from 'react';
import { Modal, Typography, Box, IconButton, Tooltip } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PreviewEditor from '../editor/PreviewEditor';
import { deletePracticeQuestion, getQuestionsByLevel } from '../../../services/adminServices/MakerPracticeServices';
import { toast } from 'react-toastify';
import Markdown from 'markdown-to-jsx';

function LevelModal({ open, onClose, rowData, getAllTopics }) {
  const [loading, setLoading] = useState(true);
  const [markdownArray, setMarkdownArray] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState('');
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const scrollRef = useRef(null);
  const questionRefs = useRef({});

  useEffect(() => {
    if (!open) {
      setMarkdownArray([]);
      setSelectedQuestionId(null);
    }
  }, [open]);

  useEffect(() => {
    if (selectedQuestionId !== null) {
      setTimeout(() => {
        questionRefs.current[selectedQuestionId]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

      }, 1000);
    }
  }, [markdownArray, selectedQuestionId]);


  const fetchLevelQuestions = async () => {
    if (rowData) {
      try {
        setLoading(true);
        const response = await getQuestionsByLevel(rowData?.moduleId, rowData?.topicId, {
          practice_type: rowData.level,
        });
        setMarkdownArray(Array.isArray(response.data) ? response.data : []);
        getAllTopics();
      } catch (error) {
        toast.error(error?.response?.data?.message || error?.message || error?.error);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchLevelQuestions();
  }, [rowData]);

  const handleEdit = (markdown, index) => {
    setSelectedContent(markdown);
    setSelectedQuestionId(index);
    setEditorOpen(true);
  };

  const handleDelete = async (markdown) => {
    try {
      const response = await deletePracticeQuestion(markdown.practiceID, rowData.moduleId, rowData.topicId);
      toast.success(response?.data?.message);
      fetchLevelQuestions();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || error?.error);
    }
  };

  const handleAddNewQuestion = () => {
    setSelectedContent('');
    setEditorOpen(true);
    setSelectedQuestionId(markdownArray.length);
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    setSelectedContent('');
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        ref={scrollRef}
        sx={{
          maxWidth: 900,
          padding: 3,
          backgroundColor: 'white',
          borderRadius: 2,
          boxShadow: 3,
          margin: 'auto',
          top: '5%',
          maxHeight: '80%',
          overflowY: 'auto',
          position: 'relative',
          outline: 'none',
        }}
      >
        <Typography
          variant="h6"
          component="h2"
          sx={{
            backgroundColor: '#1976d2',
            color: 'white',
            padding: '10px 20px',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
            marginBottom: 1,
          }}
        >
          {rowData?.level} Questions
        </Typography>

        {loading ? (
          <Typography>Loading...</Typography>
        ) : (
          markdownArray?.map((markdown, index) => (
            <Box
              key={index}
              ref={(el) => (questionRefs.current[index] = el)}
              sx={{
                marginBottom: 1,
                padding: 2,
                border: '1px solid #ccc',
                borderRadius: '8px',
                backgroundColor: '#f9f9f9',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'grey' }}>
                  Question {index + 1}
                </Typography>
                <Box>
                  <Tooltip title="Edit Question" placement="top">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEdit(markdown, index)}
                      sx={{
                        margin: '0px 10px',
                        '&:hover': {
                          backgroundColor: '#1976d2',
                          color: 'white',
                        },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Question" placement="top">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(markdown)}
                      sx={{
                        '&:hover': {
                          backgroundColor: '#d32f2f',
                          color: 'white',
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <Markdown
                options={{
                  overrides: {
                    img: {
                      props: {
                        style: {
                          maxWidth: '100%',
                          borderRadius: '8px',
                          marginTop: '10px',
                          marginBottom: '10px',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        },
                      },
                    },
                  },
                }}
              >
                {markdown.practice_md_path}
              </Markdown>
            </Box>
          ))
        )}

        <Tooltip title="Add New Question" placement="top" arrow>
          <IconButton
            color="primary"
            onClick={handleAddNewQuestion}
            sx={{
              position: 'relative',
              bottom: 0,
              left: '95%',
              backgroundColor: '#1976d2',
              color: 'white',
              '&:hover': {
                backgroundColor: '#145ca6',
              },
              boxShadow: 2,
            }}
          >
            <AddCircleOutlineIcon fontSize="large" />
          </IconButton>
        </Tooltip>

        <PreviewEditor
          openModal={editorOpen}
          setOpenModal={setEditorOpen}
          handleCloseModal={handleEditorClose}
          initialContent={selectedContent}
          rowData={rowData}
          setEditorOpen={setEditorOpen}
          fetchLevelQuestions={fetchLevelQuestions}
        />
      </Box>
    </Modal>
  );
}

export default LevelModal;
